import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * useSonioxLive — real-time customer-audio transcription via Soniox.
 *
 * Captures a (customer) MediaStream, streams it to Soniox's real-time STT
 * WebSocket using a short-lived temporary key minted by our backend, and
 * surfaces a live transcript. Fires `onUtterance(fullText)` whenever Soniox
 * detects the end of a customer utterance (endpoint detection) so callers can
 * auto-refresh a suggested reply.
 *
 * @param {MediaStream|null} stream  remote/customer audio stream
 * @param {boolean} enabled          start/stop transcription
 * @param {(fullText:string)=>void} onUtterance  called on each utterance end
 */
export default function useSonioxLive(stream, enabled, onUtterance) {
  const [finalText, setFinalText] = useState("");   // committed customer transcript
  const [interim, setInterim] = useState("");        // in-flight (non-final) words
  const [status, setStatus] = useState("idle");      // idle|connecting|live|error|stopped

  const wsRef = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);                   // WebAudio graph for capture
  const watchdogRef = useRef(null);                   // audio-energy monitor timer
  const finalRef = useRef("");                        // mirror of finalText for callbacks
  const lastFiredLenRef = useRef(0);                  // transcript length at last utterance fire
  const onUtteranceRef = useRef(onUtterance);
  const stoppingRef = useRef(false);
  const utterTimerRef = useRef(null);                 // silence-debounce fallback trigger
  onUtteranceRef.current = onUtterance;

  const fireUtterance = useCallback(() => {
    if (utterTimerRef.current) { clearTimeout(utterTimerRef.current); utterTimerRef.current = null; }
    const full = finalRef.current.trim();
    // The words finalized SINCE the previous fire = the customer's current
    // utterance. Callers answer this (not the whole running transcript).
    const latest = finalRef.current.slice(lastFiredLenRef.current).trim();
    lastFiredLenRef.current = finalRef.current.length;
    if (full && onUtteranceRef.current) {
      // eslint-disable-next-line no-console
      console.info("[c7][soniox] utterance →", (latest || full).slice(-80));
      onUtteranceRef.current(full, latest);
    }
  }, []);

  const cleanup = useCallback(() => {
    stoppingRef.current = true;
    if (utterTimerRef.current) { clearTimeout(utterTimerRef.current); utterTimerRef.current = null; }
    if (watchdogRef.current) { clearInterval(watchdogRef.current); watchdogRef.current = null; }
    try {
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
    } catch { /* ignore */ }
    recorderRef.current = null;
    try {
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== "closed") ctx.close();
    } catch { /* ignore */ }
    audioCtxRef.current = null;
    try {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send("");          // graceful end — empty TEXT frame flushes finals (NOT an empty binary frame)
        // Give Soniox a short grace window to return pending finals before
        // closing; closing in the same tick can truncate the last final (~100ms).
        setTimeout(() => { try { ws.close(); } catch { /* ignore */ } }, 600);
      } else if (ws) {
        try { ws.close(); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    wsRef.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!stream || wsRef.current) return;
    // Only transcribe if the stream actually carries audio tracks.
    if (!stream.getAudioTracks || stream.getAudioTracks().length === 0) return;

    stoppingRef.current = false;
    setStatus("connecting");
    let key;
    try {
      const r = await api.post("/ai/soniox/temp-key");
      key = r.data;
    } catch {
      setStatus("error");
      return;
    }

    const ws = new WebSocket(key.websocket_url || "wss://stt-rt.soniox.com/transcribe-websocket");
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        api_key: key.api_key,
        model: key.model || "stt-rt-v5",
        audio_format: "auto",
        enable_endpoint_detection: true,
        language_hints: ["en"],
      }));

      // Pick a supported container/codec for MediaRecorder.
      const candidates = [
        "audio/webm;codecs=opus", "audio/webm",
        "audio/ogg;codecs=opus", "audio/ogg",
      ];
      let mime;
      for (const c of candidates) {
        if (window.MediaRecorder?.isTypeSupported?.(c)) { mime = c; break; }
      }

      // CRITICAL: MediaRecorder attached DIRECTLY to a remote WebRTC receiver
      // MediaStream produces silent/near-empty WebM in Chromium (no PCM energy),
      // so Soniox receives bytes but returns zero tokens. Route the remote
      // stream through a WebAudio graph and record the graph's destination
      // stream instead — that yields real audio samples.
      let recordStream = stream;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          audioCtxRef.current = ctx;
          // A suspended context (autoplay policy) processes no audio, so the
          // destination stream would be silent → zero tokens. Resume it now and,
          // if still blocked, resume on the next user gesture.
          const resume = () => { if (ctx.state !== "running") ctx.resume().catch(() => {}); };
          resume();
          if (ctx.state !== "running") {
            const onGesture = () => {
              resume();
              document.removeEventListener("click", onGesture, true);
              document.removeEventListener("keydown", onGesture, true);
            };
            document.addEventListener("click", onGesture, true);
            document.addEventListener("keydown", onGesture, true);
          }
          const src = ctx.createMediaStreamSource(stream);
          const dest = ctx.createMediaStreamDestination();
          src.connect(dest);
          recordStream = dest.stream;

          // Audio-energy watchdog: the "LISTENING but no words" bug is almost
          // always a SILENT capture (suspended context / stream latched to
          // silence / customer line muted). Measure RMS; if there's no energy
          // for ~4s, surface a "no-audio" state so it stops being invisible.
          try {
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            src.connect(analyser);
            const buf = new Float32Array(analyser.fftSize);
            let silentMs = 0;
            watchdogRef.current = setInterval(() => {
              analyser.getFloatTimeDomainData(buf);
              let peak = 0;
              for (let i = 0; i < buf.length; i++) {
                const v = Math.abs(buf[i]); if (v > peak) peak = v;
              }
              if (peak < 0.004) {
                silentMs += 700;
                if (silentMs >= 4000) setStatus((s) => (s === "live" ? "no-audio" : s));
              } else {
                silentMs = 0;
                setStatus((s) => (s === "no-audio" ? "live" : s));
              }
            }, 700);
          } catch { /* analyser optional */ }
        }
      } catch { /* fall back to the raw stream */ }

      let rec;
      try {
        rec = new MediaRecorder(recordStream, mime ? { mimeType: mime } : undefined);
      } catch {
        setStatus("error");
        return;
      }
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (ws.readyState === WebSocket.OPEN && e.data && e.data.size > 0) {
          e.data.arrayBuffer().then((buf) => {
            try { ws.send(buf); } catch { /* ignore */ }
          });
        }
      };
      rec.start(250); // emit a chunk every 250ms for low latency
      setStatus("live");
    };

    ws.onmessage = (evt) => {
      if (typeof evt.data !== "string") return;
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      if (msg.error_code || msg.error_type) {
        // eslint-disable-next-line no-console
        console.warn("[c7][soniox] error:", msg.error_code, msg.error_type, msg.error_message || "");
        setStatus("error");
        return;
      }

      let endpoint = false;
      let newFinal = "";
      let curInterim = "";
      for (const t of (msg.tokens || [])) {
        const txt = t.text || "";
        // Endpoint-detection / control tokens (e.g. "<end>", "<fin>") are markers,
        // not transcript text.
        if (txt === "<end>" || txt === "<fin>") { if (t.is_final) endpoint = true; continue; }
        if (t.is_final) newFinal += txt;
        else curInterim += txt;
      }
      if (newFinal) {
        finalRef.current = (finalRef.current + newFinal);
        setFinalText(finalRef.current);
      }
      setInterim(curInterim);

      if (endpoint) {
        // Semantic endpoint → fire immediately (most reliable signal).
        setInterim("");
        fireUtterance();
      } else if (newFinal) {
        // Fallback: some calls produce sparse <end> tokens. Whenever the
        // customer adds finalized words, (re)arm a short silence timer so the
        // suggested reply still refreshes ~1.5s after they stop talking.
        if (utterTimerRef.current) clearTimeout(utterTimerRef.current);
        utterTimerRef.current = setTimeout(fireUtterance, 1500);
      }
    };

    ws.onerror = () => { if (!stoppingRef.current) setStatus("error"); };
    ws.onclose = () => { if (!stoppingRef.current) setStatus("stopped"); };
  }, [stream]);

  useEffect(() => {
    if (enabled && stream) {
      start();
    } else {
      cleanup();
      setStatus("idle");
    }
    return cleanup;
  }, [enabled, stream, start, cleanup]);

  const reset = useCallback(() => {
    finalRef.current = "";
    lastFiredLenRef.current = 0;
    setFinalText("");
    setInterim("");
  }, []);

  return { finalText, interim, status, reset };
}
